import { fabric } from "fabric";
import { MiddleDragState } from "../types";
import { useBoardToolbarStore } from "../../../helper/store/toolbar";

/**
 * =====================================================
 * 中键拖拽平移模块
 * =====================================================
 * 功能：按住鼠标中键（滚轮）拖动来平移画布视口
 */

/**
 * 创建中键拖拽平移状态
 */
export const createMiddleDragState = (): MiddleDragState => ({
  isDragging: false,
  lastPosX: 0,
  lastPosY: 0
});

/**
 * 处理中键按下 - 开始拖拽
 * @param canvas Fabric 画布实例
 * @param state 拖拽状态
 * @param boardToolbarStore 工具栏状态 store
 * @param lockTargetFind 锁定目标查找的回调
 * @param e 鼠标事件
 */
export const handleMiddleMouseDown = (
  canvas: fabric.Canvas,
  state: MiddleDragState,
  boardToolbarStore: ReturnType<typeof useBoardToolbarStore>,
  lockTargetFind: () => void,
  e: MouseEvent
): void => {
  boardToolbarStore.startTemporaryDrag({ panViaPlugin: false });
  state.isDragging = true;
  state.lastPosX = e.clientX;
  state.lastPosY = e.clientY;
  canvas.setCursor("grabbing");
  lockTargetFind();
};

/**
 * 处理中键松开 - 结束拖拽
 * @param canvas Fabric 画布实例
 * @param state 拖拽状态
 * @param boardToolbarStore 工具栏状态 store
 * @param unlockTargetFind 解锁目标查找的回调
 */
export const handleMiddleMouseUp = (
  canvas: fabric.Canvas,
  state: MiddleDragState,
  boardToolbarStore: ReturnType<typeof useBoardToolbarStore>,
  unlockTargetFind: () => void
): void => {
  boardToolbarStore.endTemporaryDrag();
  state.isDragging = false;
  canvas.setCursor("default");
  canvas.requestRenderAll();
  unlockTargetFind();

  // 立即同步坐标偏移与可见对象 setCoords，避免中键平移后第一次点击选不中
  canvas.calcOffset();
  (canvas as any).__lastCalcOffsetTs = performance.now();
  canvas.fire("viewport:flushSync", { e: null });
  // 兼容仍监听 viewport:transform 的覆盖层（如 GenerationCard）
  canvas.fire("viewport:transform", { e: null });
};

/**
 * 处理中键拖拽移动
 * @param canvas Fabric 画布实例
 * @param state 拖拽状态
 * @param debouncedCalcOffset 防抖的坐标偏移计算函数（拖拽结束后调用）
 * @param e 鼠标事件
 */
export const handleMiddleDragMove = (
  canvas: fabric.Canvas,
  state: MiddleDragState,
  debouncedCalcOffset: () => void,
  e: MouseEvent
): void => {
  if (!state.isDragging) return;

  const vpt = canvas.viewportTransform;
  if (vpt) {
    const newVpt = [...vpt];
    newVpt[4] += e.clientX - state.lastPosX;
    newVpt[5] += e.clientY - state.lastPosY;
    canvas.setViewportTransform(newVpt);
    canvas.requestRenderAll();
    canvas.fire("viewport:transform", { e });
    state.lastPosX = e.clientX;
    state.lastPosY = e.clientY;

    // 防抖调用作为后备（拖拽结束时再次确认）
    debouncedCalcOffset();
  }
};
